import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Plus, Trash2, Edit2, Eye, TableProperties, Loader2, Share2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ShareSection } from '../../shared/ShareSection';
import { OrangeCheckbox, QRPreviewModal } from '../../shared/WizardComponents';
import { QRThumbnail } from '../../thermal-insulations/ProductPrintView';
import { Input } from '../../../components/ui/input';

/**
 * Reusable list page for Division 26 modules.
 * Configurable via `config` prop for columns, toolbar, and module-specific behavior.
 *
 * Props:
 *  - config: { moduleName, apiBase, columns: [{key, label, width?, render?}] }
 *  - projectId, csiItem, canEditCSIItems, canManageProject
 *  - share props (shareCompanySearch, etc.)
 *  - onCreateClick: () => void
 *  - onEditClick: (item) => void
 */
export const Division26ListPage = ({
  config,
  items,
  loading,
  projectId,
  csiItem,
  canEditCSIItems,
  canManageProject,
  onCreateClick,
  onEditClick,
  onReviewClick,
  onScheduleClick,
  onDelete,
  onBulkDelete,
  filterBar = null,
  filteredItems = null,
  extraToolbarButtons = null,
  showShare = true,
  // Share props
  shareCompanySearch, showCompanySuggestions, companySuggestions,
  highlightedIndex, searchingCompanies,
  handleSearchCompanies, handleCompanySearchKeyDown,
  setShowCompanySuggestions, setHighlightedIndex,
  handleSelectCompany, handleUnshareCSI,
  project, user,
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false });
  const [showShareModal, setShowShareModal] = useState(false);
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, name: '' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { setSelectedIds(new Set()); }, [items]);

  const filtered = filteredItems != null ? filteredItems : (searchTerm
    ? items.filter(item => {
        const term = searchTerm.toLowerCase();
        return config.columns.some(col => {
          const val = item[col.key];
          return val && String(val).toLowerCase().includes(term);
        });
      })
    : items);

  const handleDeleteItem = (item) => {
    setConfirmState({
      open: true,
      title: `Delete ${config.moduleName}`,
      description: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        setDeleting(item.id);
        try {
          await onDelete(item.id);
          toast.success(`${config.moduleName} deleted`);
        } catch {
          toast.error(`Failed to delete ${config.moduleName.toLowerCase()}`);
        } finally {
          setDeleting(null);
        }
        setConfirmState({ open: false });
      },
    });
  };

  const handleBulkDeleteItems = () => {
    const count = selectedIds.size;
    if (count === 0) return;
    setConfirmState({
      open: true,
      title: `Delete selected ${config.moduleName.toLowerCase()}s?`,
      description: `You are about to delete ${count} item(s). This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          const deleted = await onBulkDelete(Array.from(selectedIds));
          setSelectedIds(new Set());
          toast.success(`Deleted ${deleted} item(s).`);
        } catch {
          toast.error('Failed to delete items');
        } finally {
          setBulkDeleting(false);
        }
        setConfirmState({ open: false });
      },
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map(p => p.id)));
  };
  const toggleSelectRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid={`${config.testIdPrefix}-loading`}>
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid={`${config.testIdPrefix}-section`}>
      {/* Search + Filter bar (single row) */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative w-56">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder={`Search ${config.moduleName.toLowerCase()}s...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-xs"
            data-testid={`${config.testIdPrefix}-search`}
          />
        </div>
        {filterBar}
      </div>

      {/* Toolbar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {showShare && canManageProject && (
            <div className="text-xs text-slate-600" data-testid={`${config.testIdPrefix}-share-info`}>
              Shared with: {csiItem?.assignedCompanyName ? (
                <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
              ) : (
                <span className="text-slate-400">N/A</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {extraToolbarButtons}
          {canManageProject && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDeleteItems}
              disabled={bulkDeleting || selectedIds.size === 0}
              data-testid={`${config.testIdPrefix}-bulk-delete-btn`}
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete All{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          )}
          {canEditCSIItems && (
            <Button
              size="sm"
              onClick={onCreateClick}
              data-testid={`${config.testIdPrefix}-create-btn`}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create {config.moduleName}
            </Button>
          )}
          {showShare && canManageProject && csiItem && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowShareModal(true)}
              data-testid={`${config.testIdPrefix}-share-btn`}
            >
              <Share2 className="h-4 w-4 mr-1.5" />
              Share
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg" data-testid={`${config.testIdPrefix}-empty`}>
          <p>{items.length === 0 ? `No ${config.moduleName.toLowerCase()}s created yet` : 'No items match the current search'}</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid={`${config.testIdPrefix}-table`}>
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canManageProject && (
                  <th className="w-10 px-3 py-3">
                    <OrangeCheckbox
                      id={`${config.testIdPrefix}-select-all`}
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleSelectAll}
                      testId={`${config.testIdPrefix}-select-all-checkbox`}
                    />
                  </th>
                )}
                {config.columns.map(col => (
                  <th key={col.key} className={`px-3 py-3 text-left font-semibold text-slate-700 ${col.width || ''}`}>
                    {col.label}
                  </th>
                ))}
                <th className="w-12 px-3 py-3 text-center font-semibold text-slate-700">QR</th>
                {canManageProject && (
                  <th className="w-40 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <tr
                    key={item.id}
                    className={`border-b transition-colors ${isSelected ? 'bg-orange-50/60' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                    data-testid={`${config.testIdPrefix}-row-${item.id}`}
                  >
                    {canManageProject && (
                      <td className="w-10 px-3 py-2">
                        <OrangeCheckbox
                          id={`${config.testIdPrefix}-select-${item.id}`}
                          checked={isSelected}
                          onChange={() => toggleSelectRow(item.id)}
                          testId={`${config.testIdPrefix}-select-row-${item.id}`}
                        />
                      </td>
                    )}
                    {config.columns.map(col => (
                      <td key={col.key} className={`px-3 py-2 ${col.cellClass || 'text-slate-700'}`}>
                        {col.render ? col.render(item) : (item[col.key] ?? '-')}
                      </td>
                    ))}
                    <td className="w-12 px-3 py-2 text-center" data-testid={`${config.testIdPrefix}-qr-${item.id}`}>
                      <button
                        type="button"
                        className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                        onClick={() => setQrModal({ open: true, qrToken: item.qrToken, name: item.name })}
                        data-testid={`${config.testIdPrefix}-qr-btn-${item.id}`}
                        title="Click to enlarge QR code"
                      >
                        <QRThumbnail qrToken={item.qrToken} size={32} />
                      </button>
                    </td>
                    {canManageProject && (
                      <td className="w-40 px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {onScheduleClick && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onScheduleClick(item)} data-testid={`${config.testIdPrefix}-schedule-${item.id}`} title="Circuit Schedule">
                              <TableProperties className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onReviewClick(item)} data-testid={`${config.testIdPrefix}-review-${item.id}`} title="Review">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onEditClick(item)} data-testid={`${config.testIdPrefix}-edit-${item.id}`} title="Edit">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => handleDeleteItem(item)} disabled={deleting === item.id} data-testid={`${config.testIdPrefix}-delete-${item.id}`} title="Delete">
                            {deleting === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Share Modal */}
      {showShare && csiItem && (
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="max-w-2xl" data-testid={`${config.testIdPrefix}-share-modal`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Share This Specification
              </DialogTitle>
              <DialogDescription>
                Share {csiItem.itemTitle} specification with a sub-contractor company
              </DialogDescription>
            </DialogHeader>
            <ShareSection
              asModal
              tabValue=""
              inputIdPrefix={config.testIdPrefix}
              csiItem={csiItem}
              project={project}
              user={user}
              shareCompanySearch={shareCompanySearch}
              showCompanySuggestions={showCompanySuggestions}
              companySuggestions={companySuggestions}
              highlightedIndex={highlightedIndex}
              searchingCompanies={searchingCompanies}
              handleSearchCompanies={handleSearchCompanies}
              handleCompanySearchKeyDown={handleCompanySearchKeyDown}
              setShowCompanySuggestions={setShowCompanySuggestions}
              setHighlightedIndex={setHighlightedIndex}
              handleSelectCompany={handleSelectCompany}
              handleUnshareCSI={handleUnshareCSI}
            />
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog {...confirmState} onClose={() => setConfirmState({ open: false })} />

      <QRPreviewModal
        open={qrModal.open}
        onClose={() => setQrModal({ open: false, qrToken: null, name: '' })}
        qrToken={qrModal.qrToken}
        productName={qrModal.name}
      />
    </div>
  );
};
